import React, { useState, useEffect } from 'react';
import { Bell, X, CheckCheck, Trash2, Clock } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { Notification } from '../types';

interface NotificationBellProps {
  currentUserId: string | null;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ currentUserId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  // --- REAL-TIME LISTENER ---
  useEffect(() => {
    if (!currentUserId) return;

    setLoading(true);
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', currentUserId),
      where('dismissed', '==', false),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Notification[];
        setNotifications(notifs);
        setLoading(false);
      },
      (error) => {
        console.error('Notification listener error:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUserId]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDismiss = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        dismissed: true,
        dismissedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Failed to dismiss:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like': return '❤️';
      case 'comment': return '💬';
      case 'birthday': return '🎂';
      case 'member_joined': return '👋';
      case 'event': return '📅';
      case 'admin_alert': return '🚨';
      default: return '📢';
    }
  };

  const getTimeAgo = (timestamp: any) => {
    try {
      const date = timestamp?.toDate?.() ? new Date(timestamp.toDate()) : new Date(timestamp);
      const now = new Date();
      const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      return `${Math.floor(seconds / 86400)}d ago`;
    } catch {
      return 'Recently';
    }
  };

  return (
    <div className="relative">
      {/* BELL BUTTON */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
        title="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* DROPDOWN PANEL */}
      {isOpen && (
           <div className="fixed left-1/2 top-16 transform -translate-x-1/2 w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 max-h-[600px] flex flex-col"
             style={{ width: '100%', maxWidth: '24rem' }}>
          {/* HEADER */}
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl flex-shrink-0">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notifications
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* NOTIFICATIONS LIST */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {loading ? (
              <div className="p-8 text-center text-gray-400 flex items-center justify-center h-40">
                <div className="animate-spin">
                  <Bell className="w-5 h-5" />
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400 flex flex-col items-center justify-center h-40">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                <p className="text-sm">All caught up!</p>
                <p className="text-xs">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-4 hover:bg-gray-50 transition-colors group ${
                    !notif.read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    {/* AVATAR */}
                    <div className="flex-shrink-0 mt-1">
                      <img
                        src={notif.actorAvatar || `https://ui-avatars.com/api/?name=${notif.actorName}&background=random`}
                        alt={notif.actorName}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                    </div>

                    {/* CONTENT */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-bold text-gray-900 flex items-center gap-1">
                            <span>{getNotificationIcon(notif.type)}</span>
                            <span>{notif.title}</span>
                          </p>
                          {notif.message && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {notif.message}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" /> {getTimeAgo(notif.timestamp)}
                          </p>
                        </div>

                        {/* ACTIONS */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {!notif.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notif.id);
                              }}
                              className="p-1 hover:bg-blue-100 rounded transition-colors"
                              title="Mark as read"
                            >
                              <CheckCheck className="w-4 h-4 text-blue-600" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismiss(notif.id);
                            }}
                            className="p-1 hover:bg-red-100 rounded transition-colors"
                            title="Dismiss"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>

                      {/* UNREAD INDICATOR */}
                      {!notif.read && (
                        <div className="mt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notif.id);
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold hover:bg-blue-200 transition-colors"
                          >
                            Mark Read
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* FOOTER */}
          {notifications.length > 0 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 rounded-b-2xl text-center flex-shrink-0">
              <button className="text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors">
                View All Notifications →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
